import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { DeleteOutlined } from '@material-ui/icons';
import { useFormikContext, Field, FieldArray, ErrorMessage } from 'formik';
import { TextField } from 'formik-material-ui';

import CustomSelectChips from 'components/CustomSelectChips';
import { IPolicy } from 'interfaces/Policy';
import { RootState } from 'store';

import { IPolicyRegisterFormProps } from './interfaces';
import AddResourceButton from './AddResourceButton';
import { useStyles } from './styles';

const PolicyRegisterForm: React.FC<IPolicyRegisterFormProps> = ({
  isViewOnly,
}: IPolicyRegisterFormProps) => {
  const classes = useStyles();
  const { operations, resources } = useSelector(
    (state: RootState) => state.permissionList,
  );
  const { permissions } = useSelector((state: RootState) => state.user);

  const { values, errors, touched } = useFormikContext<IPolicy>();

  const getResourceLabel = useCallback(
    (id: string) => {
      const resource = resources.find((r) => r._id === id);
      return resource?.name || 'Resource inválido';
    },
    [resources],
  );

  return (
    <Grid
      item
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
    >
      {!isViewOnly && (
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          alignItems="stretch"
        >
          <Grid item>
            <Typography
              data-cy="policy-register-form__label--policy-name"
              variant="subtitle1"
            >
              Nome do Perfil
            </Typography>
          </Grid>
          <Field
            data-cy="policy-register-form__input--policy-name"
            component={TextField}
            fullWidth
            name="name"
            disabled={isViewOnly}
          />
        </Grid>
      )}
      <FieldArray
        name="permissions"
        render={(arrayHelpers) => (
          <Grid>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              data-cy="policy-register-form__container--permissions"
            >
              <Grid item>
                <Typography
                  data-cy="policy-register-form__label--permissions"
                  variant="subtitle1"
                >
                  Permissões
                </Typography>
              </Grid>
              {!isViewOnly && (
                <Grid
                  data-cy="policy-register-form__button--add-permissions"
                  item
                >
                  <AddResourceButton push={arrayHelpers.push} />
                </Grid>
              )}
              {typeof errors.permissions === 'string' && touched.permissions && (
                <FormHelperText error>
                  <ErrorMessage name="permissions" />
                </FormHelperText>
              )}
            </Grid>
            {values.permissions.map((permission, index) => (
              <Grid
                key={permission.resource}
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                className={classes.resourceGrid}
                style={{ paddingBottom: '5px' }}
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  wrap="nowrap"
                >
                  <Grid item>
                    <Typography
                      data-cy="policy-register-form__label--resources"
                      variant="subtitle2"
                    >
                      {`Recurso: ${getResourceLabel(permission.resource)}`}
                    </Typography>
                  </Grid>
                  {!isViewOnly && permissions['policy:delete'] && (
                    <IconButton onClick={() => arrayHelpers.remove(index)}>
                      <DeleteOutlined color="primary" />
                    </IconButton>
                  )}
                </Grid>
                <Grid
                  data-cy="policy-register-form__custom-select-chips--resources"
                  item
                >
                  <CustomSelectChips
                    isViewOnly={isViewOnly || !permissions['policy:delete']}
                    list={operations.map((operation) => ({
                      value: operation._id,
                      label: operation.name,
                    }))}
                    fieldName={`permissions[${index}].operations`}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
      />
    </Grid>
  );
};

export default PolicyRegisterForm;
